import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import authFetch from '../../utils/authFetch';
import { strBasePath, strPropertyQuery, useStrProperty } from './properties';

const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocal ? 'http://localhost:5000' : 'https://bdinteldb.com';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
// Monday-first display order mapped to JS/Postgres day-of-week numbers (0 = Sunday)
const WEEKDAY_OPTIONS = [
  { label: 'Mon', dow: 1 },
  { label: 'Tue', dow: 2 },
  { label: 'Wed', dow: 3 },
  { label: 'Thu', dow: 4 },
  { label: 'Fri', dow: 5 },
  { label: 'Sat', dow: 6 },
  { label: 'Sun', dow: 0 }
];
const ALL_WEEKDAYS = WEEKDAY_OPTIONS.map((w) => w.dow);

const toIsoDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const parseIsoDate = (iso) => {
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
};

const formatPrice = (value) => `${Number(value).toLocaleString('sl-SI', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;

const PricingPage = () => {
  const navigate = useNavigate();
  const property = useStrProperty();
  const propertyKey = property.key;
  const today = useMemo(() => new Date(), []);
  const todayIso = toIsoDate(today);

  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth()); // 0-indexed

  const [prices, setPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);

  const [rangeFrom, setRangeFrom] = useState('');
  const [rangeTo, setRangeTo] = useState('');
  const [priceInput, setPriceInput] = useState('');
  const [weekdays, setWeekdays] = useState(ALL_WEEKDAYS);

  const [bookings, setBookings] = useState([]);
  const [feesDraft, setFeesDraft] = useState({ cleaningFee: '', petFee: '', touristTaxAdult: '', touristTaxChild: '' });
  const [savingFees, setSavingFees] = useState(false);
  const [feesError, setFeesError] = useState('');
  const [feesNotice, setFeesNotice] = useState('');
  const feesLoadedRef = useRef(false);

  const monthStartIso = toIsoDate(new Date(viewYear, viewMonth, 1));
  const monthEndIso = toIsoDate(new Date(viewYear, viewMonth + 1, 0));

  const loadPrices = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/short-term-rentals/prices${strPropertyQuery(propertyKey)}&from=${monthStartIso}&to=${monthEndIso}`);
      if (!response.ok) throw new Error('Failed to load prices');
      const data = await response.json();
      const map = {};
      (data.prices || []).forEach((p) => { map[p.price_date] = p.price; });
      setPrices(map);
      const fees = data.fees || {};
      if (feesLoadedRef.current) return;
      feesLoadedRef.current = true;
      const feeToInput = (value) => (value !== null && value !== undefined ? String(value) : '');
      setFeesDraft({
        cleaningFee: feeToInput(fees.cleaningFee),
        petFee: feeToInput(fees.petFee),
        touristTaxAdult: feeToInput(fees.touristTaxAdult),
        touristTaxChild: feeToInput(fees.touristTaxChild)
      });
    } catch (err) {
      setError(err.message || 'Failed to load prices');
    } finally {
      setLoading(false);
    }
  }, [monthStartIso, monthEndIso, propertyKey]);

  useEffect(() => {
    loadPrices();
  }, [loadPrices]);

  useEffect(() => {
    const loadBookings = async () => {
      try {
        const response = await authFetch(`${API_BASE}/api/short-term-rentals/bookings${strPropertyQuery(propertyKey)}`);
        if (!response.ok) throw new Error('Failed to load bookings');
        const data = await response.json();
        setBookings(data.bookings || []);
      } catch (err) {
        setError(err.message || 'Failed to load bookings');
      }
    };
    loadBookings();
  }, [propertyKey]);

  // ISO date -> confirmed booking occupying that night (check_in inclusive, check_out exclusive)
  const confirmedNights = useMemo(() => {
    const map = {};
    bookings.filter((b) => b.status === 'confirmed').forEach((b) => {
      const end = parseIsoDate(b.check_out);
      const cursor = parseIsoDate(b.check_in);
      while (cursor < end) {
        map[toIsoDate(cursor)] = b;
        cursor.setDate(cursor.getDate() + 1);
      }
    });
    return map;
  }, [bookings]);

  const goToPrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const goToNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const goToToday = () => {
    setViewYear(today.getFullYear());
    setViewMonth(today.getMonth());
  };

  const calendarCells = useMemo(() => {
    const firstOfMonth = new Date(viewYear, viewMonth, 1);
    const firstWeekday = (firstOfMonth.getDay() + 6) % 7;
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

    const cells = [];
    for (let i = 0; i < firstWeekday; i += 1) {
      cells.push(null);
    }
    for (let day = 1; day <= daysInMonth; day += 1) {
      cells.push(new Date(viewYear, viewMonth, day));
    }
    while (cells.length % 7 !== 0) {
      cells.push(null);
    }
    return cells;
  }, [viewYear, viewMonth]);

  // First click sets the start date, second click sets the end date; a further click starts a new range.
  const handleDayClick = (iso) => {
    setNotice('');
    if (!rangeFrom || (rangeFrom && rangeTo && rangeFrom !== rangeTo)) {
      setRangeFrom(iso);
      setRangeTo(iso);
      setPriceInput(prices[iso] !== undefined ? String(prices[iso]) : '');
      return;
    }
    if (iso < rangeFrom) {
      setRangeTo(rangeFrom);
      setRangeFrom(iso);
    } else {
      setRangeTo(iso);
    }
  };

  const isInSelection = (iso) => rangeFrom && rangeTo && iso >= rangeFrom && iso <= rangeTo;

  const toggleWeekday = (dow) => {
    setWeekdays((prev) => (prev.includes(dow) ? prev.filter((d) => d !== dow) : [...prev, dow]));
  };

  const clearSelection = () => {
    setRangeFrom('');
    setRangeTo('');
    setPriceInput('');
    setWeekdays(ALL_WEEKDAYS);
  };

  const submitPrices = async (price) => {
    setError('');
    setNotice('');
    if (!rangeFrom || !rangeTo) {
      setError('Select a start and end date.');
      return;
    }
    if (rangeFrom < todayIso) {
      setError('Prices can only be set for today or future dates.');
      return;
    }
    if (weekdays.length === 0) {
      setError('Select at least one weekday.');
      return;
    }
    if (price !== null && (priceInput === '' || Number(priceInput) < 0)) {
      setError('Enter a valid price.');
      return;
    }

    setSaving(true);
    try {
      const response = await authFetch(`${API_BASE}/api/short-term-rentals/prices`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          propertyKey,
          from: rangeFrom,
          to: rangeTo,
          price,
          weekdays: weekdays.length === ALL_WEEKDAYS.length ? undefined : weekdays
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Failed to save prices');
      const skippedText = data.skippedBooked ? ` Skipped ${data.skippedBooked} confirmed booked night(s).` : '';
      setNotice((price === null
        ? `Cleared prices for ${data.affected} night(s).`
        : `Saved ${formatPrice(price)} for ${data.affected} night(s).`) + skippedText);
      await loadPrices();
    } catch (err) {
      setError(err.message || 'Failed to save prices');
    } finally {
      setSaving(false);
    }
  };

  const saveFees = async () => {
    setFeesError('');
    setFeesNotice('');
    setSavingFees(true);
    try {
      const response = await authFetch(`${API_BASE}/api/short-term-rentals/price-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...feesDraft, propertyKey })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Failed to save fees');
      setFeesNotice('Fees saved.');
    } catch (err) {
      setFeesError(err.message || 'Failed to save fees');
    } finally {
      setSavingFees(false);
    }
  };

  const monthPriceValues = Object.values(prices);
  const monthMin = monthPriceValues.length ? Math.min(...monthPriceValues) : null;
  const monthMax = monthPriceValues.length ? Math.max(...monthPriceValues) : null;

  return (
    <div>
      <button onClick={() => navigate(strBasePath(property))} style={backButtonStyle}>
        <ArrowLeft size={16} /> Back
      </button>

      <h1 style={{ color: '#1e293b', marginBottom: '6px' }}>Pricing</h1>
      <p style={{ color: '#64748b', marginBottom: '30px' }}>Set nightly prices for future dates at {property.siteName}.</p>

      <div style={{ ...cardStyle, marginBottom: '20px' }}>
        <h3 style={{ marginTop: 0, color: '#1e293b' }}>Fees</h3>
        <p style={{ color: '#64748b', fontSize: '13px', marginTop: 0 }}>
          Cleaning and pet fees are charged once per stay. Tourist tax is charged per person per night.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
          <div>
            <label style={labelStyle}>Cleaning fee (€)</label>
            <input type="number" min="0" step="0.01" value={feesDraft.cleaningFee} onChange={(e) => setFeesDraft({ ...feesDraft, cleaningFee: e.target.value })} style={inputStyle} placeholder="e.g. 40" />
          </div>
          <div>
            <label style={labelStyle}>Pet fee (€)</label>
            <input type="number" min="0" step="0.01" value={feesDraft.petFee} onChange={(e) => setFeesDraft({ ...feesDraft, petFee: e.target.value })} style={inputStyle} placeholder="e.g. 20" />
          </div>
          <div>
            <label style={labelStyle}>Tourist tax per adult / day (€)</label>
            <input type="number" min="0" step="0.01" value={feesDraft.touristTaxAdult} onChange={(e) => setFeesDraft({ ...feesDraft, touristTaxAdult: e.target.value })} style={inputStyle} placeholder="e.g. 2.50" />
          </div>
          <div>
            <label style={labelStyle}>Tourist tax per child / day (€)</label>
            <input type="number" min="0" step="0.01" value={feesDraft.touristTaxChild} onChange={(e) => setFeesDraft({ ...feesDraft, touristTaxChild: e.target.value })} style={inputStyle} placeholder="e.g. 1.25" />
          </div>
        </div>
        {feesError && <p style={{ color: '#dc2626', marginBottom: 0 }}>{feesError}</p>}
        {feesNotice && <p style={{ color: '#059669', marginBottom: 0 }}>{feesNotice}</p>}
        <div style={{ marginTop: '16px' }}>
          <button onClick={saveFees} disabled={savingFees} style={saveButtonStyle}>{savingFees ? 'Saving...' : 'Save fees'}</button>
        </div>
      </div>

      <div style={{ ...cardStyle, marginBottom: '20px' }}>
        <h3 style={{ marginTop: 0, color: '#1e293b' }}>Set nightly price</h3>
        <p style={{ color: '#64748b', fontSize: '13px', marginTop: 0 }}>
          Click a day in the calendar to start a selection and another day to end it, or pick the dates below.
          Nights already covered by a confirmed booking keep their current price.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
          <div>
            <label style={labelStyle}>From</label>
            <input
              type="date"
              min={todayIso}
              value={rangeFrom}
              onChange={(e) => {
                setRangeFrom(e.target.value);
                if (!rangeTo || e.target.value > rangeTo) setRangeTo(e.target.value);
              }}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>To</label>
            <input type="date" min={rangeFrom || todayIso} value={rangeTo} onChange={(e) => setRangeTo(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Price per night (€)</label>
            <input type="number" min="0" step="0.01" value={priceInput} onChange={(e) => setPriceInput(e.target.value)} style={inputStyle} placeholder="e.g. 120" />
          </div>
        </div>

        <div style={{ marginTop: '14px' }}>
          <label style={labelStyle}>Apply to weekdays</label>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {WEEKDAY_OPTIONS.map((w) => {
              const active = weekdays.includes(w.dow);
              return (
                <button
                  key={w.dow}
                  type="button"
                  onClick={() => toggleWeekday(w.dow)}
                  style={{ ...weekdayToggleStyle, ...(active ? weekdayToggleActiveStyle : {}) }}
                >
                  {w.label}
                </button>
              );
            })}
          </div>
        </div>

        {error && <p style={{ color: '#dc2626', marginBottom: 0 }}>{error}</p>}
        {notice && <p style={{ color: '#059669', marginBottom: 0 }}>{notice}</p>}

        <div style={{ display: 'flex', gap: '10px', marginTop: '16px', flexWrap: 'wrap' }}>
          <button onClick={() => submitPrices(Number(priceInput))} disabled={saving} style={saveButtonStyle}>
            {saving ? 'Saving...' : 'Save price'}
          </button>
          <button onClick={() => submitPrices(null)} disabled={saving} style={clearPriceButtonStyle}>Clear prices</button>
          <button onClick={clearSelection} disabled={saving} style={cancelButtonStyle}>Reset selection</button>
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button onClick={goToPrevMonth} style={navButtonStyle}><ChevronLeft size={18} /></button>
            <h2 style={{ margin: 0, color: '#1e293b', fontSize: '18px', minWidth: '160px', textAlign: 'center' }}>
              {MONTH_NAMES[viewMonth]} {viewYear}
            </h2>
            <button onClick={goToNextMonth} style={navButtonStyle}><ChevronRight size={18} /></button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            {monthMin !== null && (
              <span style={{ color: '#64748b', fontSize: '13px' }}>
                {monthMin === monthMax ? formatPrice(monthMin) : `${formatPrice(monthMin)} – ${formatPrice(monthMax)}`} this month
              </span>
            )}
            <button onClick={goToToday} style={todayButtonStyle}>Today</button>
          </div>
        </div>

        {loading ? (
          <p style={{ color: '#64748b' }}>Loading...</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: '700px' }}>
              <div style={weekHeaderRowStyle}>
                {WEEKDAY_NAMES.map((wd) => (
                  <div key={wd} style={weekHeaderCellStyle}>{wd}</div>
                ))}
              </div>
              <div style={gridStyle}>
                {calendarCells.map((date, idx) => {
                  if (!date) {
                    return <div key={`empty-${idx}`} style={emptyCellStyle} />;
                  }
                  const iso = toIsoDate(date);
                  const isPast = iso < todayIso;
                  const price = prices[iso];
                  const selected = isInSelection(iso);
                  const bookedBy = confirmedNights[iso];
                  return (
                    <div
                      key={iso}
                      role={isPast ? undefined : 'button'}
                      tabIndex={isPast ? undefined : 0}
                      onClick={isPast ? undefined : () => handleDayClick(iso)}
                      onKeyDown={isPast ? undefined : (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleDayClick(iso); } }}
                      style={{
                        ...dayCellStyle,
                        ...(isPast ? pastCellStyle : { cursor: 'pointer' }),
                        ...(bookedBy ? bookedCellStyle : {}),
                        ...(iso === todayIso ? todayCellStyle : {}),
                        ...(selected ? selectedCellStyle : {})
                      }}
                      title={bookedBy ? `Booked: ${bookedBy.full_name} - price locked` : undefined}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '4px' }}>
                        <span style={dayNumberStyle}>{date.getDate()}</span>
                        {bookedBy && <span style={bookedTagStyle}>Booked</span>}
                      </div>
                      <div style={{ ...priceTextStyle, color: price !== undefined ? '#1e293b' : '#cbd5e1' }}>
                        {price !== undefined ? formatPrice(price) : '—'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const backButtonStyle = { display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: '14px', marginBottom: '20px', padding: 0 };
const cardStyle = { backgroundColor: 'white', padding: '25px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', border: '1px solid #e2e8f0' };
const navButtonStyle = { display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: 'white', cursor: 'pointer', color: '#334155' };
const todayButtonStyle = { padding: '6px 14px', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: 'white', cursor: 'pointer', color: '#334155', fontSize: '13px' };
const weekHeaderRowStyle = { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: '6px' };
const weekHeaderCellStyle = { textAlign: 'center', fontSize: '12px', fontWeight: 600, color: '#64748b', padding: '6px 0' };
const gridStyle = { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '6px' };
const dayCellStyle = { minHeight: '70px', border: '1px solid #f1f5f9', borderRadius: '8px', padding: '6px', backgroundColor: '#fafafa', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', userSelect: 'none' };
const pastCellStyle = { opacity: 0.45, cursor: 'not-allowed' };
const todayCellStyle = { border: '1px solid #2563eb', backgroundColor: '#eff6ff' };
const bookedCellStyle = { backgroundColor: '#f0fdf4', border: '1px dashed #86efac' };
const bookedTagStyle = { fontSize: '10px', fontWeight: 600, color: '#15803d', backgroundColor: '#dcfce7', borderRadius: '4px', padding: '1px 5px' };
const selectedCellStyle = { border: '1px solid #059669', backgroundColor: '#dcfce7' };
const emptyCellStyle = { minHeight: '70px' };
const dayNumberStyle = { fontSize: '12px', fontWeight: 600, color: '#94a3b8' };
const priceTextStyle = { fontSize: '15px', fontWeight: 600, textAlign: 'right' };
const labelStyle = { display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px', fontWeight: 600 };
const inputStyle = { width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '14px', boxSizing: 'border-box' };
const weekdayToggleStyle = { padding: '6px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: 'white', color: '#64748b', cursor: 'pointer', fontSize: '13px' };
const weekdayToggleActiveStyle = { backgroundColor: '#eff6ff', borderColor: '#93c5fd', color: '#1d4ed8', fontWeight: 600 };
const saveButtonStyle = { backgroundColor: '#059669', color: 'white', border: 'none', borderRadius: '8px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px', fontWeight: 600 };
const clearPriceButtonStyle = { backgroundColor: '#fef2f2', color: '#dc2626', border: 'none', borderRadius: '8px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px' };
const cancelButtonStyle = { backgroundColor: '#f1f5f9', color: '#334155', border: 'none', borderRadius: '8px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px' };

export default PricingPage;
